/**
 * Comportamento de acessibilidade dos diálogos (modais) — implementação única.
 *
 * Existem dois tipos de modal no sistema: os que usam o `ModalConsulte` (a maioria) e
 * cinco que montam o próprio markup por serem controlados pela store
 * (`ModalFiltroEstudante`, `ModalFiltroFuncionario`, `ModalFiltroTurma`,
 * `ModalAvisosNotificacoes`, `ModalLegado`). Os cinco eram cópias do markup do
 * `ModalConsulte` e herdaram os mesmos defeitos — sem foco preso, sem devolução de foco,
 * sem trava de rolagem e com `aria-labelledby` apontando para um id que não existe.
 *
 * Este módulo concentra o comportamento para que corrigir um lugar corrija todos.
 *
 * É um módulo de funções, e não um composable `useXxx()`, de propósito: os componentes
 * de modal do projeto usam a Options API e não têm `setup()`, então um composable com
 * ganchos de ciclo de vida não serviria a eles sem reescrita.
 *
 * O que cada diálogo precisa fazer:
 *
 *   open()   -> const anterior = abrirDialogo(elDialogo)
 *   close()  -> fecharDialogo(anterior)
 *   @keydown -> prenderFoco(evento, elDialogo)
 *
 * O que fica de fora daqui, e por quê: fechar no Esc continua no template de cada
 * diálogo (`@keydown.esc="close"`), porque só o componente sabe se fechar é permitido
 * naquele contexto (há modais de confirmação que não devem fechar sem resposta).
 */

/**
 * Tudo que pode receber foco por Tab.
 *
 * `[tabindex="-1"]` fica de fora de propósito: é focável por script, não por teclado — é
 * o caso do próprio contêiner do diálogo.
 */
export const SELETOR_FOCAVEL = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  'details > summary',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Quantos diálogos estão abertos AGORA.
 *
 * É um contador de módulo (e não estado por componente) porque a trava de rolagem é do
 * documento, e há telas que abrem um modal de dentro de outro: com um booleano por
 * instância, fechar o de cima devolveria a rolagem enquanto o de baixo ainda está aberto.
 */
let dialogosAbertos = 0;

let sequencial = 0;

/** Id único por diálogo, para o `aria-labelledby` apontar para o título certo. */
export function novoIdTitulo(prefixo = 'dialogo'): string {
  sequencial += 1;
  return `${prefixo}-titulo-${sequencial}`;
}

/** Só o que está de fato visível: um modal costuma ter blocos escondidos por v-show ou
 *  em aba inativa, e mandar o foco para um deles faz o cursor desaparecer. */
function estaVisivel(el: HTMLElement): boolean {
  return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
}

export function focaveisDentro(raiz: HTMLElement | null | undefined): HTMLElement[] {
  if (!raiz) return [];
  return Array.from(raiz.querySelectorAll<HTMLElement>(SELETOR_FOCAVEL)).filter(estaVisivel);
}

/**
 * Abre o diálogo: guarda quem tinha o foco, trava a rolagem do fundo e leva o foco para
 * dentro. Devolve o elemento que estava focado, para o `fecharDialogo` restaurar.
 *
 * O foco vai para o primeiro controle ÚTIL, e não para o botão de fechar (que é o
 * primeiro no DOM, por estar no cabeçalho). Sem isso, abrir um modal de filtro deixava o
 * usuário de teclado em cima do "X" — precisando de um Tab a mais para começar a
 * preencher, em toda abertura. Se a tela marcar um campo com `data-autofocus`, ele tem
 * prioridade; não havendo nenhum controle, o foco fica no próprio diálogo, que é o que
 * permite ao Esc e ao Tab funcionarem a partir dali.
 */
export function abrirDialogo(dialogo: HTMLElement | null | undefined): HTMLElement | null {
  const elementoAnterior = document.activeElement as HTMLElement | null;

  dialogosAbertos += 1;
  document.body.classList.add('modal-consulte-aberto');

  if (dialogo) {
    const preferido = dialogo.querySelector<HTMLElement>('[data-autofocus]');
    const candidatos = focaveisDentro(dialogo);
    const primeiroUtil = candidatos.find((el) => !el.classList.contains('btn-close'));

    (preferido ?? primeiroUtil ?? candidatos[0] ?? dialogo).focus();
  }

  return elementoAnterior;
}

/**
 * Fecha o diálogo: libera a rolagem e devolve o foco a quem o abriu (WCAG 2.4.3).
 *
 * Sem a devolução, o foco volta para o `<body>` e o Tab seguinte recomeça do topo da
 * página — quem navega por teclado perde o lugar a cada modal fechado.
 *
 * O `document.contains` é porque o elemento de origem às vezes já saiu do DOM: é o caso
 * do botão de ação de uma linha da grade, quando a grade recarrega depois de salvar.
 * Focar um nó órfão não faz nada, e o foco ficaria no `<body>`.
 */
export function fecharDialogo(elementoAnterior: HTMLElement | null | undefined): void {
  dialogosAbertos = Math.max(0, dialogosAbertos - 1);
  if (dialogosAbertos === 0) {
    document.body.classList.remove('modal-consulte-aberto');
  }

  if (elementoAnterior && document.contains(elementoAnterior)) {
    elementoAnterior.focus();
  }
}

/**
 * Mantém o Tab girando DENTRO do diálogo (WCAG 2.1.2 — sem armadilha, mas também sem
 * fuga). Antes, tabular a partir do último controle levava o foco para a tela que ficou
 * atrás do overlay: o usuário de teclado continuava "andando" por campos que não
 * conseguia ver, e não tinha como voltar.
 *
 * Ligar no contêiner do diálogo: `@keydown="(e) => prenderFoco(e, $refs.dialogRef)"`.
 */
export function prenderFoco(evento: KeyboardEvent, dialogo: HTMLElement | null | undefined): void {
  if (evento.key !== 'Tab') return;

  /*
   * Modal aberto de dentro de outro modal: o de dentro está aninhado no DOM do de fora,
   * então este mesmo handler roda duas vezes (o evento borbulha). O de dentro trata
   * primeiro e marca o evento; aqui o de fora sai de cena.
   */
  if (evento.defaultPrevented) return;
  if (!dialogo) return;

  /*
   * Offcanvas aberto DENTRO do diálogo (o painel "Filtros"/"Colunas" do ConsulteGrid) tem
   * escopo visual próprio: enquanto está aberto, o Tab tem de girar SÓ nos controles dele,
   * não no grid que ficou atrás. Sem isto o foco escapava para o conteúdo obscurecido pelo
   * offcanvas — quem usa teclado "andava" por campos que não conseguia ver (2.4.11 / 2.1.2).
   * O offcanvas do Bootstrap ganha a classe `show` ao abrir; havendo mais de um, vale o
   * último no DOM (o de cima). Fechado, a armadilha volta a ser o diálogo inteiro.
   */
  const offcanvasAbertos = dialogo.querySelectorAll<HTMLElement>('.offcanvas.show');
  const raiz = offcanvasAbertos.length
    ? offcanvasAbertos[offcanvasAbertos.length - 1]
    : dialogo;

  const focaveis = focaveisDentro(raiz);

  if (focaveis.length === 0) {
    evento.preventDefault();
    raiz.focus();
    return;
  }

  const primeiro = focaveis[0];
  const ultimo = focaveis[focaveis.length - 1];
  const ativo = document.activeElement as HTMLElement | null;
  const focoEstaDentro = !!ativo && raiz.contains(ativo) && ativo !== raiz;

  if (evento.shiftKey) {
    if (!focoEstaDentro || ativo === primeiro) {
      evento.preventDefault();
      ultimo.focus();
    }
    return;
  }

  if (!focoEstaDentro || ativo === ultimo) {
    evento.preventDefault();
    primeiro.focus();
  }
}
